/**
 * timezone.ts — site-local time, using only the standard Intl API (works in Workers, Node and browsers).
 *
 * A site has an IANA timezone (e.g. "Asia/Karachi"). The server stores UTC instants; these helpers convert at
 * the edges. The "wall clock" trick: `toWall(instant, tz)` returns a Date whose LOCAL getters (getHours(), …)
 * show the site's clock, so the calendar math that already uses local getters works unchanged for any site
 * timezone. `fromWall` is its inverse. Never call `.toISOString()` on a wall Date; convert back with `fromWall`.
 */

const validCache = new Map<string, boolean>();

/** True for a zone name Intl accepts ("UTC", "Europe/London", "Asia/Karachi"). */
export function isValidTimeZone(tz: string): boolean {
  if (!tz || tz.length > 64) return false;
  const hit = validCache.get(tz);
  if (hit !== undefined) return hit;
  let ok = true;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
  } catch {
    ok = false;
  }
  validCache.set(tz, ok);
  return ok;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    fmtCache.set(tz, f);
  }
  return f;
}

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** The calendar and clock reading in `tz` at the instant `ms`. */
export function zonedParts(ms: number, tz: string): ZonedParts {
  const out: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(new Date(ms))) if (p.type !== "literal") out[p.type] = Number(p.value);
  return { year: out.year!, month: out.month!, day: out.day!, hour: out.hour! % 24, minute: out.minute!, second: out.second! };
}

/** Milliseconds that `tz` is ahead of UTC at the instant `ms` (negative when behind). */
export function tzOffsetMs(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** The UTC instant at which `tz` shows this wall-clock time. Handles daylight-saving changes. */
export function zonedToInstant(p: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number }, tz: string): number {
  const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour ?? 0, p.minute ?? 0, p.second ?? 0);
  let guess = wallAsUtc - tzOffsetMs(wallAsUtc, tz);
  guess = wallAsUtc - tzOffsetMs(guess, tz);
  return guess;
}

/** A Date whose local getters read as the site's wall clock at `instant`. */
export function toWall(instant: Date | string | number, tz: string): Date {
  const p = zonedParts(new Date(instant).getTime(), tz);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Inverse of toWall: the real instant (ISO, UTC) at which the site's clock shows this Date's local reading. */
export function fromWall(wall: Date, tz: string): string {
  return new Date(
    zonedToInstant(
      { year: wall.getFullYear(), month: wall.getMonth() + 1, day: wall.getDate(), hour: wall.getHours(), minute: wall.getMinutes(), second: wall.getSeconds() },
      tz
    )
  ).toISOString();
}

/** Start of the site's calendar day containing `now`, as an ISO UTC instant. */
export function startOfZonedDayIso(now: Date, tz: string): string {
  const p = zonedParts(now.getTime(), tz);
  return new Date(zonedToInstant({ year: p.year, month: p.month, day: p.day }, tz)).toISOString();
}

/** Start of the site's week (Monday by default, or Sunday) containing `now`, as an ISO UTC instant. */
export function startOfZonedWeekIso(now: Date, tz: string, weekStartsOn: 0 | 1 = 1): string {
  const p = zonedParts(now.getTime(), tz);
  const dow = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay(); // 0 = Sunday
  const back = (dow - weekStartsOn + 7) % 7;
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day - back));
  return new Date(zonedToInstant({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }, tz)).toISOString();
}

/** "+05:00" style label for the zone at an instant, for showing next to times. */
export function utcOffsetLabel(now: Date, tz: string): string {
  const mins = Math.round(tzOffsetMs(now.getTime(), tz) / 60_000);
  const sign = mins < 0 ? "-" : "+";
  const abs = Math.abs(mins);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}
